import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import {
  AccountBalance,
  ArrowDownward,
  ArrowUpward,
  AssignmentTurnedIn,
  CalendarMonth,
  ErrorOutline,
  Insights,
  Payments,
  Schedule,
  TrendingUp,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import Programa from '../../../components/Programa/Programa'
import Table from '../../../components/Table'
import { metodoPrograma } from '../../../components/api'
import ModalContaAReceber from '../contaReceber/ModalContaAReceber'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
)
const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
const compactMoney = (value) =>
  new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 1,
  }).format(Number(value || 0))
const colors = {
  green: '#0f9d72',
  red: '#e25555',
  blue: '#3975d5',
  amber: '#d98b20',
  navy: '#17243c',
  muted: '#6b778c',
}
const EMPTY_LIST = []
const chartBase = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'bottom',
      labels: { usePointStyle: true, boxWidth: 8, padding: 18 },
    },
    tooltip: {
      callbacks: {
        label: (ctx) => `${ctx.dataset.label || ctx.label}: ${money(ctx.raw)}`,
      },
    },
  },
  scales: {
    x: { grid: { display: false }, border: { display: false } },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: '#eef1f6' },
      ticks: { callback: compactMoney },
    },
  },
}

const CardIndicador = ({ titulo, valor, legenda, cor, icon, alerta }) => (
  <Paper
    elevation={0}
    sx={{
      height: '100%',
      p: 2.25,
      border: '1px solid #e7eaf0',
      borderRadius: 3,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        bgcolor: cor,
      }}
    />
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Box>
        <Typography
          sx={{
            fontSize: 12,
            color: colors.muted,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {titulo}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 23, lg: 27 },
            color: colors.navy,
            fontWeight: 750,
            mt: 0.7,
          }}
        >
          {valor}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2,
          bgcolor: `${cor}18`,
          color: cor,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {icon}
      </Box>
    </Stack>
    <Typography
      sx={{ fontSize: 12, color: alerta ? cor : colors.muted, mt: 1 }}
    >
      {legenda}
    </Typography>
  </Paper>
)

const Painel = ({ titulo, subtitulo, children, height = 330 }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      border: '1px solid #e7eaf0',
      borderRadius: 3,
      height: '100%',
    }}
  >
    <Typography sx={{ color: colors.navy, fontWeight: 750, fontSize: 16 }}>
      {titulo}
    </Typography>
    <Typography sx={{ color: colors.muted, fontSize: 12, mt: 0.4, mb: 2.2 }}>
      {subtitulo}
    </Typography>
    <Box sx={{ height }}>{children}</Box>
  </Paper>
)

const DashboardFinanceiro = () => {
  const [modal, setModal] = useState(false)
  const [dados, setDados] = useState(null)
  const [lista, setLista] = useState([])
  const [dashboard, setDashboard] = useState({})
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const carregar = useCallback(async () => {
    setLoading(true)
    setErro('')
    try {
      const [indicadores, movimentacoes] = await Promise.all([
        metodoPrograma('getIndicadores', {}),
        metodoPrograma('lista', {}),
      ])
      if (!indicadores.status)
        throw new Error(
          indicadores.dsErro || 'Não foi possível carregar os indicadores.',
        )
      setDashboard(indicadores)
      if (movimentacoes.status) setLista(movimentacoes.lista || [])
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar o dashboard.')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    carregar()
  }, [carregar, modal])
  const resumo = dashboard.resumoGerencial || {}
  const eficiencia = dashboard.eficienciaCobranca || {}
  const qualidade = dashboard.qualidadeClassificacao || {}
  const evolucao = dashboard.evolucaoMensal || EMPTY_LIST
  const projecao = dashboard.fluxoProjetado || EMPTY_LIST
  const categorias = dashboard.despesasPorCategoria || EMPTY_LIST
  const aging = dashboard.agingRecebiveis || EMPTY_LIST
  const formas = dashboard.recebimentosPorForma || EMPTY_LIST
  const contasBancarias = dashboard.saldoContasBancarias || EMPTY_LIST
  const centrosCusto = dashboard.despesasPorCentroCusto || EMPTY_LIST
  const devedores = dashboard.maioresDevedores || EMPTY_LIST
  const dadosEvolucao = useMemo(
    () => ({
      labels: evolucao.map((x) => x.periodo),
      datasets: [
        {
          label: 'Receitas',
          data: evolucao.map((x) => x.entradas),
          borderColor: colors.green,
          backgroundColor: '#0f9d7218',
          fill: true,
          tension: 0.35,
        },
        {
          label: 'Despesas',
          data: evolucao.map((x) => x.saidas),
          borderColor: colors.red,
          backgroundColor: '#e2555512',
          fill: true,
          tension: 0.35,
        },
      ],
    }),
    [evolucao],
  )
  const dadosProjecao = useMemo(
    () => ({
      labels: projecao.map((x) => x.periodo),
      datasets: [
        {
          label: 'Entradas previstas',
          data: projecao.map((x) => x.entradas),
          backgroundColor: colors.green,
          borderRadius: 5,
        },
        {
          label: 'Saídas previstas',
          data: projecao.map((x) => x.saidas),
          backgroundColor: colors.red,
          borderRadius: 5,
        },
      ],
    }),
    [projecao],
  )
  const dadosCategoria = useMemo(
    () => ({
      labels: categorias.map((x) => x.label),
      datasets: [
        {
          label: 'Despesas',
          data: categorias.map((x) => x.valor),
          backgroundColor: [
            '#3975d5',
            '#5b8fe2',
            '#7ca7ea',
            '#9dbef0',
            '#bed5f6',
            '#d9e6fa',
          ],
          borderRadius: 6,
        },
      ],
    }),
    [categorias],
  )
  const dadosAging = useMemo(
    () => ({
      labels: aging.map((x) => x.label),
      datasets: [
        {
          data: aging.map((x) => x.valor),
          backgroundColor: [
            colors.green,
            '#e5ae47',
            '#e58b47',
            '#df6952',
            '#ba4354',
          ],
          borderWidth: 0,
        },
      ],
    }),
    [aging],
  )
  const dadosFormas = useMemo(
    () => ({
      labels: formas.map((x) => x.label),
      datasets: [
        {
          data: formas.map((x) => x.valor),
          backgroundColor: [
            '#3975d5',
            '#0f9d72',
            '#8b65cc',
            '#e5ae47',
            '#e25555',
            '#56a6b8',
          ],
          borderWidth: 0,
        },
      ],
    }),
    [formas],
  )
  const dadosContas = useMemo(
    () => ({
      labels: contasBancarias.map((x) => x.label),
      datasets: [
        {
          label: 'Saldo estimado',
          data: contasBancarias.map((x) => x.valor),
          backgroundColor: contasBancarias.map((x) =>
            Number(x.valor) >= 0 ? colors.green : colors.red,
          ),
          borderRadius: 6,
        },
      ],
    }),
    [contasBancarias],
  )
  const dadosCentros = useMemo(
    () => ({
      labels: centrosCusto.map((x) => x.label),
      datasets: [
        {
          label: 'Despesas',
          data: centrosCusto.map((x) => x.valor),
          backgroundColor: '#8b65cc',
          borderRadius: 6,
        },
      ],
    }),
    [centrosCusto],
  )
  return (
    <Programa
      codigo="CtrlDashboardFinanceiro"
      ferramentas={[]}
      habTrocaLocal={false}
    >
      <Box sx={{ bgcolor: '#f6f8fb', minHeight: '100%', p: { xs: 2, md: 3 } }}>
        <ModalContaAReceber
          dados={dados}
          show={modal}
          handleClose={() => setModal(false)}
          setModal={setModal}
        />
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ fontSize: 25, fontWeight: 800, color: colors.navy }}
          >
            Visão financeira
          </Typography>
          <Typography sx={{ color: colors.muted, fontSize: 13 }}>
            Resultados, compromissos e saúde do caixa em um só lugar
          </Typography>
        </Box>
        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ height: 320, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Receitas realizadas"
                  valor={money(resumo.receitasMes)}
                  legenda="Recebido no mês atual"
                  cor={colors.green}
                  icon={<ArrowUpward />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Despesas realizadas"
                  valor={money(resumo.despesasMes)}
                  legenda="Pago no mês atual"
                  cor={colors.red}
                  icon={<ArrowDownward />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Resultado do mês"
                  valor={money(resumo.resultadoMes)}
                  legenda={
                    Number(resumo.resultadoMes) >= 0
                      ? 'Operação superavitária'
                      : 'Despesas acima das receitas'
                  }
                  cor={
                    Number(resumo.resultadoMes) >= 0 ? colors.blue : colors.red
                  }
                  icon={<Insights />}
                  alerta={Number(resumo.resultadoMes) < 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Saldo projetado · 30 dias"
                  valor={money(resumo.saldoProjetado30Dias)}
                  legenda={`${money(resumo.receber30Dias)} a receber · ${money(resumo.pagar30Dias)} a pagar`}
                  cor={
                    Number(resumo.saldoProjetado30Dias) >= 0
                      ? colors.green
                      : colors.red
                  }
                  icon={<CalendarMonth />}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <CardIndicador
                  titulo="Recebíveis em atraso"
                  valor={money(resumo.receberAtrasado)}
                  legenda="Exige ação de cobrança"
                  cor={colors.red}
                  icon={<ErrorOutline />}
                  alerta
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CardIndicador
                  titulo="Inadimplência"
                  valor={`${Number(resumo.inadimplenciaPercentual || 0).toFixed(1)}%`}
                  legenda="Parcela vencida da carteira em aberto"
                  cor={colors.amber}
                  icon={<TrendingUp />}
                  alerta
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <CardIndicador
                  titulo="Contas a pagar vencidas"
                  valor={money(resumo.pagarAtrasado)}
                  legenda="Compromissos vencidos e não pagos"
                  cor={colors.red}
                  icon={<Payments />}
                  alerta
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Recebimentos no prazo"
                  valor={`${Number(eficiencia.recebimentosNoPrazoPercentual || 0).toFixed(1)}%`}
                  legenda="Baixas dos últimos 90 dias"
                  cor={colors.green}
                  icon={<AssignmentTurnedIn />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Prazo médio de recebimento"
                  valor={`${Number(eficiencia.prazoMedioRecebimento || 0).toFixed(0)} dias`}
                  legenda={`Atraso médio: ${Number(eficiencia.atrasoMedioRecebimento || 0).toFixed(0)} dias`}
                  cor={colors.blue}
                  icon={<Schedule />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Comissões em aberto"
                  valor={money(dashboard.comissoesEmAberto)}
                  legenda="Obrigações provisionadas e não pagas"
                  cor={colors.amber}
                  icon={<Payments />}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <CardIndicador
                  titulo="Qualidade da classificação"
                  valor={`${Number(qualidade.completudePercentual || 0).toFixed(1)}%`}
                  legenda={`${qualidade.totalAnalisado || 0} títulos analisados`}
                  cor={
                    Number(qualidade.completudePercentual) >= 90
                      ? colors.green
                      : colors.amber
                  }
                  icon={<AccountBalance />}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} lg={7}>
                <Painel
                  titulo="Receitas x despesas"
                  subtitulo="Evolução dos valores realizados nos últimos 6 meses"
                >
                  <Line data={dadosEvolucao} options={chartBase} />
                </Painel>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Painel
                  titulo="Fluxo de caixa projetado"
                  subtitulo="Entradas e saídas previstas por semana"
                >
                  <Bar data={dadosProjecao} options={chartBase} />
                </Painel>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} lg={5}>
                <Painel
                  titulo="Maiores despesas por categoria"
                  subtitulo="Onde o dinheiro foi gasto no mês"
                  height={300}
                >
                  <Bar
                    data={dadosCategoria}
                    options={{ ...chartBase, indexAxis: 'y' }}
                  />
                </Painel>
              </Grid>
              <Grid item xs={12} md={6} lg={3.5}>
                <Painel
                  titulo="Idade dos recebíveis"
                  subtitulo="Carteira aberta por faixa de atraso"
                  height={300}
                >
                  <Doughnut
                    data={dadosAging}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '68%',
                      plugins: chartBase.plugins,
                    }}
                  />
                </Painel>
              </Grid>
              <Grid item xs={12} md={6} lg={3.5}>
                <Painel
                  titulo="Formas de recebimento"
                  subtitulo="Composição das receitas do mês"
                  height={300}
                >
                  <Doughnut
                    data={dadosFormas}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '68%',
                      plugins: chartBase.plugins,
                    }}
                  />
                </Painel>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} lg={6}>
                <Painel
                  titulo="Posição por conta bancária"
                  subtitulo="Saldo estimado a partir das movimentações registradas"
                  height={300}
                >
                  <Bar
                    data={dadosContas}
                    options={{ ...chartBase, indexAxis: 'y' }}
                  />
                </Painel>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Painel
                  titulo="Despesas por centro de custo"
                  subtitulo="Áreas que mais consumiram recursos no mês"
                  height={300}
                >
                  <Bar
                    data={dadosCentros}
                    options={{ ...chartBase, indexAxis: 'y' }}
                  />
                </Painel>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} lg={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: '1px solid #e7eaf0',
                    borderRadius: 3,
                    height: '100%',
                  }}
                >
                  <Typography
                    sx={{ color: colors.navy, fontWeight: 750, fontSize: 16 }}
                  >
                    Maiores devedores
                  </Typography>
                  <Typography
                    sx={{ color: colors.muted, fontSize: 12, mt: 0.4, mb: 2 }}
                  >
                    Prioridade de cobrança por valor vencido
                  </Typography>
                  {devedores.length === 0 ? (
                    <Typography
                      sx={{ color: colors.muted, py: 3, textAlign: 'center' }}
                    >
                      Nenhum recebível vencido.
                    </Typography>
                  ) : (
                    devedores.map((item, index) => (
                      <Stack
                        key={`${item.nome}-${index}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          py: 1.25,
                          borderBottom:
                            index < devedores.length - 1
                              ? '1px solid #eef1f6'
                              : 0,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 2,
                              bgcolor: '#e2555514',
                              color: colors.red,
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: colors.navy,
                              }}
                            >
                              {item.nome}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 11, color: colors.muted }}
                            >
                              {item.titulos} título(s) · até{' '}
                              {item.maiorAtrasoDias} dias de atraso
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: colors.red,
                          }}
                        >
                          {money(item.valor)}
                        </Typography>
                      </Stack>
                    ))
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: '1px solid #e7eaf0',
                    borderRadius: 3,
                    height: '100%',
                  }}
                >
                  <Typography
                    sx={{ color: colors.navy, fontWeight: 750, fontSize: 16 }}
                  >
                    Qualidade dos dados
                  </Typography>
                  <Typography
                    sx={{ color: colors.muted, fontSize: 12, mt: 0.4, mb: 2 }}
                  >
                    Pendências que reduzem a precisão dos relatórios
                  </Typography>
                  {[
                    ['Sem categoria', qualidade.semCategoria],
                    ['Sem plano de contas', qualidade.semPlanoConta],
                    ['Sem centro de custo', qualidade.semCentroCusto],
                    ['Sem conta bancária', qualidade.semContaBancaria],
                  ].map(([label, valor]) => (
                    <Stack
                      key={label}
                      direction="row"
                      justifyContent="space-between"
                      sx={{ py: 1.35, borderBottom: '1px solid #eef1f6' }}
                    >
                      <Typography sx={{ fontSize: 13, color: colors.muted }}>
                        {label}
                      </Typography>
                      <Chip
                        label={valor || 0}
                        size="small"
                        color={Number(valor) > 0 ? 'warning' : 'success'}
                      />
                    </Stack>
                  ))}
                </Paper>
              </Grid>
            </Grid>
            <Paper
              elevation={0}
              sx={{ p: 2.5, border: '1px solid #e7eaf0', borderRadius: 3 }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 750, color: colors.navy }}>
                    Movimentações recentes
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: colors.muted }}>
                    Últimos pagamentos e recebimentos registrados
                  </Typography>
                </Box>
                <Chip label={`${lista.length} lançamentos`} size="small" />
              </Stack>
              <Table
                dados={lista}
                head={[
                  'Documento',
                  'Descrição',
                  'Categoria',
                  'Forma',
                  'Vencimento',
                  'Valor',
                  'Liquidado',
                ]}
                line={(item) => [
                  item[5],
                  item[7],
                  item[1]?.[1],
                  item[2]?.[1],
                  item[11] ? dayjs(item[11]).format('DD/MM/YYYY') : '',
                  <Stack
                    key={`valor-${item[0]}`}
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      color: item[6] === 'P' ? colors.red : colors.green,
                      fontWeight: 800,
                    }}
                  >
                    {item[6] === 'P' ? (
                      <ArrowDownward sx={{ fontSize: 16 }} />
                    ) : (
                      <ArrowUpward sx={{ fontSize: 16 }} />
                    )}
                    {money(item[15])}
                  </Stack>,
                  money(item[17]),
                ]}
                columnOptions={[]}
                onClick={(_, linha) => {
                  if (linha?.[6] === 'R') {
                    setDados(linha)
                    setModal(true)
                  }
                }}
              />
            </Paper>
          </>
        )}
      </Box>
    </Programa>
  )
}
export default DashboardFinanceiro
